import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import MelbiVial from "@/components/MelbiVial";
import FullPricing from "@/components/FullPricing";
import useSEO from "@/lib/useSEO";
import { goToProductIntake, PRODUCT_INTAKE_URLS } from "@/lib/intake";
import { getTreatment } from "@/lib/treatments";

// Compact horizontal-row selector. Product-specific Bask intake links live
// in PRODUCT_INTAKE_URLS in /lib/intake.js.
const ROWS = [
  { slug: "weight-loss",       product: "GLP-1 & GLP-1/GIP Injection",       label: "Weight Management Support" },
  { slug: "glp-nad-bundles",   product: "GLP-1 + NAD+ BUNDLE",     label: "Weight management + NAD+" },
  { slug: "vitality",          product: "NAD+",        label: "Vitality support" },
  { slug: "recovery",          product: "Sermorelin",  label: "Recovery support" },
  { slug: "antioxidant",       product: "Glutathione", label: "Antioxidant support" },
  { slug: "metabolic-support", product: "MIC + B12",   label: "Metabolic support" },
  { slug: "energy",            product: "B12",         label: "Energy support" },
  { slug: "skin-care",         product: "GHK-Cu",      label: "Skin care support" },
];

export default function ChooseYourTreatment() {
  const navigate = useNavigate();
  useSEO({
    title: "Online Wellness Treatments | Melbi Wellness",
    description:
      "Explore clinician-reviewed online wellness treatments from Melbi Wellness, including weight management, NAD+, Sermorelin, Glutathione, MIC + B12, B12, and topical peptide skin care support with GHK-Cu. Prescriptions are only provided if medically appropriate.",
    image: "https://melbiwellness.com/og/default.png?v=2",
    url: "https://melbiwellness.com/choose-your-treatment",
  });

  return (
    <div data-testid="choose-your-treatment-page" className="bg-[#FAF6EF] min-h-[70vh]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-16 md:pt-14 md:pb-20">
        {/* HEADER */}
        <header className="max-w-3xl">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#8A9A92]">
            Choose Your Treatment
          </p>
          <h1
            data-testid="choose-treatment-h1"
            className="mt-2 font-display text-3xl sm:text-4xl md:text-5xl font-bold leading-[1.05] tracking-tight text-[#2C2B29]"
          >
            The Wellness Edit
          </h1>
          <p className="mt-3 font-display text-lg md:text-xl text-[#2C2B29] leading-snug">
            Choose your treatment area to begin.
          </p>
          <p
            data-testid="choose-treatment-compliance"
            className="mt-2 text-xs md:text-sm text-[#8A9A92] leading-relaxed max-w-2xl"
          >
            All requests are reviewed by a licensed clinician. Prescriptions
            are only provided if medically appropriate.
          </p>
        </header>

        {/* ROWS */}
        <ul
          data-testid="choose-treatment-list"
          className="mt-8 md:mt-10 border-t border-[#E8E5DF]"
        >
          {ROWS.map((r) => {
            const hasLink = Boolean(PRODUCT_INTAKE_URLS[r.slug]);
            const treatment = getTreatment(r.slug);
            const options = treatment?.pricing?.options;
            const price = treatment?.pricing?.startingAt;
            const primaryVialSlug = treatment?.primaryVialSlug || r.slug;
            const showSpray = r.slug === "vitality" || r.slug === "antioxidant";
            const secondaryVialSlug = treatment?.dualVialOnCard || showSpray ? treatment?.secondaryVialSlug : null;
            return (
              <li key={r.slug} className="border-b border-[#E8E5DF]">
                <button
                  type="button"
                  data-testid={`choose-treatment-card-${r.slug}`}
                  onClick={() =>
                    hasLink
                      ? goToProductIntake(r.slug)
                      : navigate(`/treatments/${r.slug}`)
                  }
                  aria-label={hasLink ? `Start intake for ${r.label}` : `Join early access list for ${r.label}`}
                  className="group w-full text-left flex items-center gap-3 sm:gap-5 py-3.5 sm:py-4 px-2 sm:px-3 -mx-2 sm:-mx-3 rounded-xl hover:bg-white transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C96A4E]/40"
                >
                  {/* Vial thumb — fixed width for every row so all text aligns */}
                  <span className="shrink-0 flex items-center justify-center w-14 h-12 sm:w-16 sm:h-14">
                    {secondaryVialSlug ? (
                      <span className="flex items-center -space-x-5 sm:-space-x-6">
                        <MelbiVial
                          slug={primaryVialSlug}
                          alt=""
                          testId={`row-vial-${r.slug}`}
                          className="h-10 sm:h-12 w-auto"
                        />
                        <MelbiVial
                          slug={secondaryVialSlug}
                          alt=""
                          testId={`row-vial-secondary-${r.slug}`}
                          className="h-10 sm:h-12 w-auto"
                        />
                      </span>
                    ) : (
                      <MelbiVial
                        slug={primaryVialSlug}
                        alt=""
                        testId={`row-vial-${r.slug}`}
                        className="h-full w-auto"
                      />
                    )}
                  </span>

                  {/* Text block */}
                  <span className="flex-1 min-w-0">
                    <span
                      data-testid={`choose-treatment-product-${r.slug}`}
                      className="block text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.18em] text-[#8A9A92]"
                    >
                      {r.product}
                    </span>
                    <h2
                      data-testid={`choose-treatment-name-${r.slug}`}
                      className="mt-0.5 font-display text-base sm:text-lg md:text-xl font-semibold tracking-tight text-[#2C2B29] leading-tight truncate"
                    >
                      {r.label}
                    </h2>
                    {options ? (
                      <span
                        data-testid={`choose-treatment-price-options-${r.slug}`}
                        className="mt-1 block space-y-2"
                      >
                        {options.map((o) => (
                          <span
                            key={o.name}
                            data-testid={`choose-treatment-price-option-${r.slug}-${o.name.toLowerCase().replace(/\s+/g, "-")}`}
                            className="block text-xs sm:text-[13px] leading-snug"
                          >
                            <span className="block font-medium text-[#2C2B29]">{o.name}:</span>
                            <span className="block">
                              <span className="font-semibold text-[#C96A4E]">${o.price}</span>
                              <span className="text-[#2C2B29] font-semibold">/month</span>
                            </span>
                            {!o.monthlyOnly && (
                              <span className="block font-normal text-[#8A9A92] text-[10px] sm:text-[11px]">
                                3-month plan
                              </span>
                            )}
                          </span>
                        ))}
                      </span>
                    ) : price && (
                      <span
                        data-testid={`choose-treatment-price-${r.slug}`}
                        className="mt-1 block text-xs sm:text-[13px] leading-snug"
                      >
                        <span className="font-semibold text-[#C96A4E]">
                          ${price}
                        </span>
                        <span className="text-[#2C2B29] font-semibold">/month</span>
                        {treatment?.pricing?.cardSuffix !== "" && (
                          <span className="block font-normal text-[#8A9A92] text-[10px] sm:text-[11px]">
                            3-month plan
                          </span>
                        )}
                      </span>
                    )}
                  </span>

                  {/* CTA */}
                  <span
                    data-testid={`choose-treatment-start-now-${r.slug}`}
                    aria-label="Start Now"
                    className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-transparent group-hover:bg-[#C96A4E] group-hover:text-white text-[#C96A4E] border border-[#C96A4E]/40 group-hover:border-[#C96A4E] px-3.5 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold transition-colors"
                  >
                    <span className="hidden sm:inline">Start Now</span>
                    <span className="sm:hidden">Start</span>
                    <ArrowRight
                      size={14}
                      className="transition-transform group-hover:translate-x-0.5"
                    />
                  </span>
                </button>
                {treatment?.fullPricing && (
                  <div className="pb-3 sm:pb-4 -mt-1">
                    <FullPricing treatment={treatment} />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
